import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RemoveIcon from "@mui/icons-material/Remove";

function getScoreColor(score) {
  if (score >= 70) return { text: "#0a936f", bg: "#e3f6ef" };
  if (score >= 50) return { text: "#d98200", bg: "#fff2da" };
  return { text: "#d92d20", bg: "#fde9e7" };
}

function getInitials(name) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getAvatarColor(name) {
  const colors = ["#7c3aed", "#0a936f", "#1f6aa5", "#d98200", "#d92d20", "#4f46e5"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function MovementChip({ movement }) {
  const abs = Math.abs(movement).toFixed(2);
  if (movement > 0.05)
    return (
      <Chip
        icon={<TrendingUpIcon style={{ fontSize: 14 }} />}
        label={`+${abs} pp`}
        size="small"
        sx={{ bgcolor: "#e3f6ef", color: "#0a936f", fontWeight: 700, fontSize: 11 }}
      />
    );
  if (movement < -0.05)
    return (
      <Chip
        icon={<TrendingDownIcon style={{ fontSize: 14 }} />}
        label={`-${abs} pp`}
        size="small"
        sx={{ bgcolor: "#fde9e7", color: "#d92d20", fontWeight: 700, fontSize: 11 }}
      />
    );
  return (
    <Chip
      icon={<RemoveIcon style={{ fontSize: 14 }} />}
      label="Stable"
      size="small"
      sx={{ bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 700, fontSize: 11 }}
    />
  );
}

function ScoreRing({ score }) {
  const { text } = getScoreColor(score);
  const pct = Math.min(score, 100);
  const circumference = 2 * Math.PI * 36;
  const offset = circumference * (1 - pct / 100);
  return (
    <Box sx={{ position: "relative", width: 90, height: 90, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg width={90} height={90} style={{ position: "absolute", top: 0, left: 0 }}>
        <circle cx={45} cy={45} r={36} fill="none" stroke="#e8edf2" strokeWidth={8} />
        <circle
          cx={45} cy={45} r={36} fill="none" stroke={text} strokeWidth={8}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 45 45)"
        />
      </svg>
      <Typography fontWeight={800} fontSize={16} color={text}>
        {score.toFixed(1)}%
      </Typography>
    </Box>
  );
}

function AgentCard({ agent, onView }) {
  const avatarColor = getAvatarColor(agent.agentName);
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        border: "1px solid #dde3eb",
        boxShadow: "0 4px 16px rgba(22,30,42,.07)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 8px 28px rgba(22,30,42,.14)" },
      }}
    >
      <CardContent sx={{ flex: 1, pb: 0 }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
          <Avatar sx={{ bgcolor: avatarColor, fontWeight: 700, width: 44, height: 44, fontSize: 16 }}>
            {getInitials(agent.agentName)}
          </Avatar>
          <Box>
            <Typography fontWeight={700} fontSize={14} color="#202832">{agent.agentName}</Typography>
            <Typography fontSize={11} color="#657282">
              {agent.dateRange.from} → {agent.dateRange.to}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <ScoreRing score={agent.latestScore} />
          <Box textAlign="right">
            <Typography fontSize={11} color="#657282" fontWeight={700} textTransform="uppercase" letterSpacing="0.08em" mb={0.5}>
              Latest Score
            </Typography>
            <MovementChip movement={agent.movement} />
            <Typography fontSize={11} color="#657282" mt={0.5}>
              Baseline: {agent.baselineScore.toFixed(2)}%
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0.5, bgcolor: "#f7f8fb", borderRadius: 2, p: 1 }}>
          {[
            { label: "Calls", value: agent.totalCalls },
            { label: "Best", value: `${agent.bestScore.toFixed(1)}%` },
            { label: "Lowest", value: `${agent.lowestScore.toFixed(1)}%` },
          ].map(({ label, value }) => (
            <Box key={label} textAlign="center">
              <Typography fontSize={11} color="#657282" fontWeight={700} textTransform="uppercase" letterSpacing="0.07em">
                {label}
              </Typography>
              <Typography fontWeight={800} fontSize={13} color="#202832">{value}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 1.5 }}>
        <Button
          fullWidth variant="contained" size="small"
          onClick={() => onView(agent)}
          sx={{ bgcolor: "#7c3aed", textTransform: "none", fontWeight: 700, borderRadius: 2, "&:hover": { bgcolor: "#6d28d9" } }}
        >
          View Report
        </Button>
      </CardActions>
    </Card>
  );
}

export default function AgentReports({ agents }) {
  const [selected, setSelected] = useState(null);

  if (!agents || agents.length === 0) return null;

  return (
    <Box>
      <Typography fontWeight={700} fontSize={14} color="#464646" mb={2}>
        Agent Adherence Performance Reports
      </Typography>

      <Grid container spacing={2}>
        {agents.map((agent) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={agent.agentName}>
            <AgentCard agent={agent} onView={setSelected} />
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullScreen
        PaperProps={{ sx: { bgcolor: "#f7f8fb" } }}
      >
        <DialogTitle
          sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            bgcolor: "rgba(255,255,255,0.96)", borderBottom: "1px solid #dde3eb",
            py: 1.5, px: 3,
          }}
        >
          <Box>
            <Typography fontWeight={700} fontSize={15}>{selected?.agentName}</Typography>
            <Typography fontSize={12} color="#657282">Adherence Performance Report</Typography>
          </Box>
          <IconButton onClick={() => setSelected(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selected && (
            <iframe
              src={selected.reportFile}
              title={`${selected.agentName} Report`}
              style={{ width: "100%", height: "100%", border: "none", minHeight: "calc(100vh - 64px)" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
